import cloneDeep from "lodash/cloneDeep";
import debounce from "lodash/debounce";
import isArray from "lodash/isArray";
import React, { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { useCookies } from "react-cookie";
import { useIntl } from "react-intl";
import { ACESFilmicToneMapping, LinearFilter, RGBAFormat, sRGBEncoding, Vector3, WebGLRenderTarget } from 'three';
import PropTypes from 'prop-types';

import { useTracking } from "../data/gtm";
import { uiColors } from "../common/styles";
import { Api } from "../utilities";
import {
    calculateCroppingFromZoom,
    getCompositionMaxCoordinates,
    getCompositionOffsetFromOrigin,
    updateCrop
} from "../utilities/composition";
import { FRAMEVOUCHER } from "../data/constants";
import {
    calculateCropZoom,
    calculateCropZoomLimit,
    calculateDpi,
    getNormalizedFrameSize,
    getPassepartoutBestFit,
} from '../utilities/frame';
import { calculateWallHeight, calculateWallWidth, generateRandomId } from "../utilities/helpers";
import {
    cameraFov,
    editMenuGapXFactor,
    editMenuOffsetYFactor,
    frameDepth,
    initialCameraFov,
    dimensionTextSizeBase,
    dimensionTextOffsetBase,
    dimensionLineOffsetBase,
    diagonalLineLengthBase,
} from '../utilities/threejsConfig';
import FramiSlider from "../components/common/FramiSlider";
import FramkyLogoNavbar from "../components/common/navigation/FramkyLogoNavbar";
import { CropIcon } from "../icons";

import { dist } from "../components/three-js/FrameTransformationHandler";
import { useComposition, useImages, useModal, useNavigation, useProduct, WorkspaceContext } from '../context';
import {
    CHECKOUT,
    FRAMESIZE_DRAWER,
    FRAMESTYLE_DRAWER,
    IMAGE_DRAWER,
    ORDER_STATUS,
    TEMPLATE_DRAWER,
    WALLBACKGROUND_DRAWER,
} from './NavigationProvider';
import CommunicationBar from "../components/studio/CommunicationBar";
import MobileWorkspaceTools from "../components/studio/MobileWorkspaceTools";
import { useAppContext, useAuth, useCompositionTemplates, useFrami } from "../context";
import { useSearchParams } from "react-router-dom";
import { usePosthog } from './PosthogProvider';
import { logError } from '../utilities/logError';

export const ZOOM_ROOM = "room";
export const ZOOM_COMPOSITION = "composition";
export const ZOOM_FRAME = "frame";
export const ZOOM_EDIT = "edit";

const FRAME = 'frame';
const PRINT = 'print';
const PRINTED_IMAGE = 'image';
const PADDING = 'padding';


export function WorkspaceProvider({
    workspaceRef,
    workspaceToolsSectionRef,
    workspaceMessagesSectionRef,
    isExportingGLTF,
    setIsExportingGLTF,
    children
}) {
    const [frameOrbitControls, setFrameOrbitControls] = useState(false);

    const [workspaceToolsSectionHeight, setWorkspaceToolsSectionHeight] = useState(0);
    // const [workspaceToolsRelativeHeight, setWorkspaceToolsRelativeHeight] = useState(0);

    const [workspaceMessagesSectionHeight, setWorkspaceMessagesSectionHeight] = useState(0);
    // const [workspaceMessagesRelativeHeight, setWorkspaceMessagesRelativeHeight] = useState(0);

    const [cameraOffsetY, setCameraOffsetY] = useState(0);
    const [cameraOffsetZ, setCameraOffsetZ] = useState(0);

    const [isAnimating, setIsAnimating] = useState(true);

    // TODO: maybe move this and component creation (slider, mobileTools, communicationBars) to MobileWorkspaceTools
    const [showTools, setShowTools] = useState(true);
    const [size, setSize] = useState({ width: 0, height: 0 });

    const [mobileToolbar, setMobileToolbar] = useState(null);
    const [cropZoomSlider, setCropZoomSlider] = useState(null);
    const [communicationBars, setCommunicationBars] = useState(null);
    const [dotsSection, setDotsSection] = useState(null);

    const [hoveredFrame, setHoveredFrame] = useState(null);
    const [nearestFrame, setNearestFrame] = useState(null);
    const [stopCameraAnimation, setStopCameraAnimation] = useState(false);
    const [updateIconSize, setUpdateIconSize] = useState(true);
    const [showActiveFrameCropHint, setShowActiveFrameCropHint] = useState(true);

    const [isGesture, setIsGesture] = useState(false);
    const [isOrbiting, setIsOrbiting] = useState(false);
    const [isScaling, setIsScaling] = useState(false);

    const [gridOffset, setGridOffset] = useState([0, 0])

    const [isEditMode, setIsEditMode] = useState(false);
    const [showEditModeFramesMenu, setShowEditModeFramesMenu] = useState(false);
    const [editModeFramesMenuPosition, setEditModeFramesMenuPosition] = useState([0, 0]);
    const [editModeFramesMenuSize, setEditModeFramesMenuSize] = useState(0);
    const [editModeFramesMenuCount, setEditModeFramesMenuCount] = useState(0);

    const [framesClipboard, setFramesClipboard] = useState(null);

    const [openNewsletterTooltip, setOpenNewsletterTooltip] = useState(false);

    const [workspaceOffset, setWorkspaceOffset] = useState({ x: 0, y: 0 });
    const [workspaceSize, setWorkspaceSize] = useState({ width: 0, height: 0 });
    const [compositionModelLoaded, setCompositionModelLoaded] = useState(false);

    const [showPhotos, setShowPhotos] = useState(true);
    const [showDimensions, setShowDimensions] = useState(true);
    const [showWallBackground, setShowWallBackground] = useState(true);

    // Add workspaceAspectRatio calculation
    const workspaceAspectRatio = useMemo(() => {
        if (workspaceSize.width && workspaceSize.height) {
            return workspaceSize.width / workspaceSize.height;
        }
        return 0; // Default value when dimensions aren't available
    }, [workspaceSize.width, workspaceSize.height]);

    const [dimensionTextSizeWorld, setDimensionTextSizeWorld] = useState(dimensionTextSizeBase);
    const [dimensionTextOffsetWorld, setDimensionTextOffsetWorld] = useState(dimensionTextOffsetBase);
    const [dimensionLineOffsetWorld, setDimensionLineOffsetWorld] = useState(dimensionLineOffsetBase);
    const [diagonalLineLengthWorld, setDiagonalLineLengthWorld] = useState(diagonalLineLengthBase);

    const [cameraPosition, setCameraPosition] = useState(new Vector3(0, 0, 0));
    const [cameraLookAt, setCameraLookAt] = useState(new Vector3(0, 0, 0));
    const [cameraFov, setCameraFov] = useState(initialCameraFov);

    const [zoomLevel, setZoomLevel] = useState(ZOOM_ROOM);


    // Obliczenie marginesów procentowych za pomocą useMemo
    const workspacePercentMargins = useMemo(() => {
        const totalHeight = workspaceSize?.height;
        const totalWidth = workspaceSize?.width;

        if (totalHeight === 0 || totalWidth === 0) {
            return [0, 0, 0, 0]; // Domyślne wartości w przypadku braku wymiarów
        }

        // const topMarginPx = workspaceMessagesSectionHeight; // Margines od góry w pikselach
        const topMarginPx = 0; // messagesSection jest poza obszarem roboczym, więc nie ma marginesu od góry
        const bottomMarginPx = workspaceToolsSectionHeight; // Margines od dołu w pikselach
        const leftMarginPx = 0; // Brak marginesu z lewej strony
        const rightMarginPx = 0; // Brak marginesu z prawej strony

        const topMarginPercent = (topMarginPx / totalHeight) * 100;
        const rightMarginPercent = (rightMarginPx / totalWidth) * 100;
        const bottomMarginPercent = (bottomMarginPx / totalHeight) * 100;
        const leftMarginPercent = (leftMarginPx / totalWidth) * 100;

        return [topMarginPercent, rightMarginPercent, bottomMarginPercent, leftMarginPercent];
    }, [workspaceSize, workspaceToolsSectionHeight, workspaceMessagesSectionHeight]);

    const calculateCameraPosition = useCallback((p1, p2, margins = true, offset = 1) => {
        const depth = (p1.z + p2.z) / 2;
        let [topMargin, rightMargin, bottomMargin, leftMargin] = [0, 0, 0, 0];
        if (margins) {
            [topMargin, rightMargin, bottomMargin, leftMargin] = workspacePercentMargins.map(m => m / 100);
        }

        const verticalMarginFactor = 1 + topMargin + bottomMargin;
        const horizontalMarginFactor = 1 + leftMargin + rightMargin;

        // const cameraWidth = (workspaceSize.width * (1 - leftMargin - rightMargin));
        // const cameraHeight = (workspaceSize.height * (1 - topMargin - bottomMargin));

        const cameraAspect = (workspaceSize.width / workspaceSize.height) || 1;

        const width = Math.abs(p2.x - p1.x);
        const height = Math.abs(p2.y - p1.y);

        const offsetY = (topMargin - bottomMargin) * height / (1 - topMargin - bottomMargin);

        const centerX = (p1.x + p2.x) / 2;
        const centerY = (p1.y + p2.y) / 2 + offsetY / 2;
        const center = new Vector3(centerX, centerY, 0);

        const fov = cameraFov * (Math.PI / 180);
        const fovh = 2 * Math.atan(Math.tan(fov / 2) * cameraAspect);

        let dx = depth + Math.abs((width / 2) / Math.tan(fovh / 2)) * horizontalMarginFactor;
        let dy = depth + Math.abs((height / 2) / Math.tan(fov / 2)) * verticalMarginFactor;
        let cameraZ = Math.max(dx, dy) * offset;

        const vector = new Vector3(centerX, centerY, cameraZ)
        return {position: vector, lookAt: center};
    }, [workspacePercentMargins, cameraFov, workspaceSize]);

    const {
        activeFrame,
        activeFrames,
        currentComp,
        editModeUpdateFrames,
        generateCompositionId,
        setActiveFrame,
        setActiveFrames,
        setCurrentComp,
        setCurrentCompId,
        setSelectedFrame,
        setParsedComps,
        updateFrameCropping,
        updateCompSize,
        setIsInEditMode,
        prepareFilteredImages,
        filteredImages,
        parsedComps,
    } = useComposition();
    const { setCurrentTemplate } = useCompositionTemplates();
    const {
        currentFrami,
        memoizedCardboardPassepartouts,
        memoizedFrameSizes,
        memoizedMaterials,
        framiWallBackground,
        memoizedWallBackgrounds,
    } = useFrami();
    const { images, baselineImages } = useImages();
    const { isMobile, smallHeightDevice, isHandheldDevice } = useAppContext();
    const { marketingAccepted } = useAuth();
    const {
        activeDrawer,
        drawerHeight,
        floatingBottomHeight,
        navbarHeight,
        setEditMode,
        openDrawer,
        setNavbarContent,
        openModelViewer
    } = useNavigation();
    const { currentVariant, setCurrentVariant, setIsGeneratingImages, setImageGenerationProgress } = useProduct();
    const [cookies, setCookie] = useCookies([
        "activeFrameCroppingHintSeen", '_seen_tour', '_seen_video', 'CookieConsent', 'newsletterTooltipSeen'
    ]);

    const { modal } = useModal();

    const cameraRef = useRef();
    const glRef = useRef();
    const sceneRef = useRef();
    const isMultiTouching = useRef(false);
    const isDragging = useRef(false);
    const [dropTargetFrame, setDropTargetFrame] = useState(null);
    const isPanning = useRef(false);
    const isSelecting = useRef(false);
    const isTransforming = useRef(false);
    const workspaceWidthRef = useRef(0);
    const workspaceHeightRef = useRef(0);
    const cameraOffsetYRef = useRef(0);
    const cameraOffsetZRef = useRef(0);

    const intl = useIntl();
    const { pushEvent } = useTracking();
    const posthog = usePosthog();

    const variant = posthog.getFeatureFlag('newsletter-signup') === 'test';
    const hangingVariant = posthog.getFeatureFlag('hanging-system-communication-v2') === 'test';
    const [searchParams, setSearchParams] = useSearchParams();

    useEffect(() => {
        setIsInEditMode(isEditMode);
    }, [isEditMode]);

    useEffect(() => {
        if (activeDrawer !== WALLBACKGROUND_DRAWER) {
            setIsScaling(false);
        }
    }, [activeDrawer]);

    useEffect(() => {
        if (
            !cookies.newsletterTooltipSeen &&
            !modal?.type &&
            !isEditMode &&
            cookies._seen_tour
            && cookies._seen_video
            && cookies.CookieConsent
            && !marketingAccepted
            && variant
        ) {
            const tooltipTimeout = setTimeout(() => {
                setOpenNewsletterTooltip(true);
            }, 10000);

            return () => clearTimeout(tooltipTimeout);
        } else {
            setOpenNewsletterTooltip(false);
        }
    }, [
        marketingAccepted,
        cookies._seen_video,
        cookies.CookieConsent,
        cookies._seen_tour,
        cookies.newsletterTooltipSeen,
        modal?.type,
        isEditMode,
        variant,
    ]);

    useEffect(() => {

        const debouncedSetWorkspaceToolsSectionHeight = debounce((height) => {
            setWorkspaceToolsSectionHeight(height);
        }, 0);

        const debouncedSetWorkspaceMessagesSectionHeight = debounce((height) => {
            setWorkspaceMessagesSectionHeight(height);
        }, 0);

        const observer = new ResizeObserver((entries) => {
            for (let entry of entries) {
                if (entry.target.className.includes('WorkspaceToolsSection__Wrapper')) {
                    const bottomMargin = entry.contentRect.height === 0 ? 0 : isHandheldDevice ? 3 : 10;
                    debouncedSetWorkspaceToolsSectionHeight(entry.contentRect.height + bottomMargin);
                }
                if (entry.target.className.includes('WorkspaceMessagesSection__SectionWrapper')) {
                    const topMargin = entry.contentRect.height === 0 ? 0 : isHandheldDevice ? 20 : 30;
                    debouncedSetWorkspaceMessagesSectionHeight(entry.contentRect.height + topMargin);
                }
            }
        });
        if (workspaceToolsSectionRef.current) {
            observer.observe(workspaceToolsSectionRef.current);
        } else {
            debouncedSetWorkspaceToolsSectionHeight(0);
        }
        if (workspaceMessagesSectionRef.current) {
            observer.observe(workspaceMessagesSectionRef.current);
        } else {
            debouncedSetWorkspaceMessagesSectionHeight(0);
        }
        return () => {
            observer.disconnect();
        };
    }, []);

    const handleCropSliderChange = (frame, zoom) => {
        const frameImage = images.find(i => i.filename === frame.image);
        if (frameImage) {
            const newCrop = calculateCroppingFromZoom(frame, frameImage, zoom);
            setShowActiveFrameCropHint(false);
            if (!cookies.activeFrameCroppingHintSeen) {
                setCookie("activeFrameCroppingHintSeen", true, { expires: 0, path: '/' });
            }

            updateFrameCropping(frame, newCrop);
        }
    }

    useEffect(() => {
        if (currentFrami) {
            let width, height;
            if (currentComp?.is_stairs) {
                width = calculateWallWidth(currentFrami.stairs_wall_shape);
                height = calculateWallHeight(currentFrami.stairs_wall_shape);
            } else {
                width = calculateWallWidth(currentFrami.wall_shape);
                height = calculateWallHeight(currentFrami.wall_shape);
            }
            setSize({ width, height });
        }
    }, [currentFrami, currentComp?.is_stairs]);

    // useEffect(() => {
    //     if ((!activeDrawer || smallHeightDevice) && currentComp) {
    //         setShowTools(true);
    //     } else {
    //         setShowTools(false);
    //     }
    // }, [smallHeightDevice, activeDrawer, currentComp]);

    useEffect(() => {
        if ((isMobile && showTools && !currentComp?.is_voucher) || (smallHeightDevice && ![ORDER_STATUS, CHECKOUT].includes(activeDrawer))) {
            setMobileToolbar(
                <MobileWorkspaceTools
                    showTools={showTools}
                />
            );
        } else {
            setMobileToolbar(null);
        }
    }, [isMobile, showTools, activeDrawer, smallHeightDevice]);

    useEffect(() => {
        if (activeFrame
            && activeFrame.image
            && !isEditMode
            && !currentComp.is_ordered
            && (!currentComp?.is_voucher || (currentComp?.is_voucher && currentComp?.voucher_type === FRAMEVOUCHER))) {
            setCropZoomSlider(
                <FramiSlider
                    $leftIcon={<CropIcon size={25} color={uiColors.PRIMARY_500} style={{ zIndex: 1 }} />}
                    sx={{
                        width: `${isMobile ? '70vw' : '26vw'}`,
                        marginTop: `${isHandheldDevice ? 0 : '20px'}`,
                        marginBottom: `${isHandheldDevice ? 0 : '10px'}`,
                        pointerEvents: 'auto'
                    }}
                    $color={currentFrami?.contrast_color}
                    min={activeFrame.calculated.crop_zoom_min} max={5}
                    value={activeFrame.calculated.crop_zoom}
                    step={0.01}
                    onChange={(_, value) => handleCropSliderChange(activeFrame, value)}
                />
            )
        } else {
            setCropZoomSlider(null);
        }
    }, [activeFrame, isMobile, isEditMode, currentComp?.voucher_type]);

    useEffect(() => {
        if (activeFrame && activeFrame.image
            && (!currentComp.is_voucher || (currentComp.is_voucher && currentComp.voucher_type === FRAMEVOUCHER))
            && (!activeDrawer || (activeDrawer !== FRAMESTYLE_DRAWER)) && !isEditMode) {
            if (activeFrame.calculated.dpi >= 150) {
                setCommunicationBars(
                    <CommunicationBar
                        type='success'
                        text={intl.formatMessage({ id: 'studio.frame.dpi.success' }, { dpi: Math.round(activeFrame.calculated.dpi) })}
                    />
                )
            } else if (activeFrame.calculated.dpi >= 100) {
                setCommunicationBars(
                    <CommunicationBar
                        type='warning'
                        text={intl.formatMessage({ id: 'studio.frame.dpi.warning' }, { dpi: Math.round(activeFrame.calculated.dpi) })}
                    />
                )
            } else {
                setCommunicationBars(
                    <CommunicationBar
                        type='error'
                        text={intl.formatMessage({ id: 'studio.frame.dpi.error' }, { dpi: Math.round(activeFrame.calculated.dpi) })}
                    />
                )
            }
        } else {
            setCommunicationBars(null);
        }
    }, [activeDrawer, activeFrame, isMobile, size, currentComp, isEditMode]);

    // useEffect(() => {
    //     if (activeFrame && !isEditMode && !currentComp?.is_voucher && !smallHeightDevice && !(
    //         currentComp?.frames_json?.length > 10 && isMobile)
    //     ) {
    //         setDotsSection(
    //             <>
    //                 <SliderDotsContainer $isMobile={isMobile} className={'framky-dots'} $notPositioned={true}>
    //                     <ul>
    //                         {currentComp.frames_json?.map((frame, i) => {
    //                             return (
    //                                 <li key={i}
    //                                     className={frame.id === activeFrame.id ? 'framky-active' : ''}>
    //                                     <button onClick={() => {
    //                                         setSelectedFrame(frame, clickMode);
    //                                         pushEventToGTM('active_frame_navigation', user);
    //                                     }}
    //                                     />
    //                                 </li>
    //                             )
    //                         })}
    //                     </ul>
    //                 </SliderDotsContainer>
    //             </>
    //         )
    //     } else {
    //         setDotsSection(null);
    //     }
    // }, [activeFrame, activeDrawer, currentComp, isMobile, clickMode, isEditMode, smallHeightDevice]);

    // useEffect(() => {
    //     if (workspaceSize?.height > 0) {
    //         const toolsRelativeHeight = workspaceToolsSectionHeight / workspaceSize?.height
    //         setWorkspaceToolsRelativeHeight(toolsRelativeHeight)
    //         const messagesRelativeHeight = workspaceMessagesSectionHeight / workspaceSize?.height
    //         setWorkspaceMessagesRelativeHeight(messagesRelativeHeight)
    //     } else {
    //         setWorkspaceToolsRelativeHeight(0)
    //         setWorkspaceMessagesRelativeHeight(0)
    //     }
    // }, [workspaceMessagesSectionHeight, workspaceToolsSectionHeight, workspaceSize?.height])

    function calculateRelativeHeights() {
        const paddingY = isMobile ? 0 : 16;
        let workspaceToolsRelativeHeight = 0;
        let workspaceMessagesRelativeHeight = 0;
        // let drawerRelativeHeight = 0;
        // let navbarRelativeHeight = 0;
        // let floatingBottomRelativeHeight = 0;
        if (workspaceHeightRef.current > 0) {
            if (workspaceToolsSectionRef?.current) {
                workspaceToolsRelativeHeight = (workspaceToolsSectionRef.current.offsetHeight - paddingY) / workspaceHeightRef.current;
            }
            if (workspaceMessagesSectionRef?.current) {
                workspaceMessagesRelativeHeight = workspaceMessagesSectionRef.current.offsetHeight / workspaceHeightRef.current;
            }
        }
        // if (isMobile && workspaceHeightRef.current > 0) {
        //     drawerRelativeHeight = drawerHeight / workspaceHeightRef.current;
        //     navbarRelativeHeight = navbarHeight / workspaceHeightRef.current;
        //     floatingBottomRelativeHeight = floatingBottomHeight / workspaceHeightRef.current;
        // }
        return {
            workspaceToolsRelativeHeight,
            workspaceMessagesRelativeHeight,
            // drawerRelativeHeight,
            // navbarRelativeHeight,
            // floatingBottomRelativeHeight,
        };
    }

    const calculateCameraOffsetY = useCallback((height, width) => {
        let {
            workspaceToolsRelativeHeight,
            workspaceMessagesRelativeHeight,
        } = calculateRelativeHeights();
        let alternativeHeight = 0
        if (workspaceHeightRef.current > 0 && workspaceWidthRef.current > 0) {
            alternativeHeight = width * (workspaceHeightRef.current * (1 - workspaceToolsRelativeHeight - workspaceMessagesRelativeHeight)) / workspaceWidthRef.current
        }
        const calculatedHeight = Math.max(height, alternativeHeight)
        return ((workspaceToolsRelativeHeight - workspaceMessagesRelativeHeight) * calculatedHeight) / (1 - workspaceToolsRelativeHeight - workspaceMessagesRelativeHeight)
    }, [activeFrame?.id, workspaceMessagesSectionHeight, workspaceToolsSectionHeight])

    const calculateCameraExtraY = useCallback((height) => {
        let {
            workspaceToolsRelativeHeight,
            workspaceMessagesRelativeHeight,
        } = calculateRelativeHeights();
        return ((workspaceToolsRelativeHeight + workspaceMessagesRelativeHeight) * height) / (1 - workspaceToolsRelativeHeight - workspaceMessagesRelativeHeight)
    }, [activeFrame?.id, workspaceMessagesSectionHeight, workspaceToolsSectionHeight])

    function handleStopAnimation() {
        setStopCameraAnimation(true);
        workspaceRef.current.removeEventListener("pointerdown", handleStopAnimation);
        workspaceRef.current.removeEventListener("wheel", handleStopAnimation);
    }

    useEffect(() => {
        if (frameOrbitControls) {
            setStopCameraAnimation(false);
            workspaceRef.current.addEventListener("pointerdown", handleStopAnimation);
            workspaceRef.current.addEventListener("wheel", handleStopAnimation);
        } else {
            setStopCameraAnimation(false);
        }
    }, [frameOrbitControls, activeFrame]);

    useEffect(() => {
        if (!isAnimating) {
            setIsAnimating(true);
        }
    }, [
        activeFrame?.id,
        workspaceSize,
        navbarHeight,
        floatingBottomHeight,
        drawerHeight,
        currentFrami?.wall_shape,
        currentFrami?.stairs_wall_shape,
        currentComp?.id,
        frameOrbitControls,
        // cameraOffsetZ,
        cameraPosition.z,
    ]);

    useEffect(() => {
        function handleTouchStart(event) {
            if (event.touches.length >= 2) {
                isMultiTouching.current = true;
            }
        }

        function handleTouchEnd() {
            if (isMultiTouching.current) {
                isMultiTouching.current = false;
            }
        }

        window.addEventListener("touchstart", handleTouchStart);
        window.addEventListener("touchend", handleTouchEnd);

        return () => {
            window.removeEventListener("touchstart", handleTouchStart);
            window.removeEventListener("touchend", handleTouchEnd);
        };
    }, [isMultiTouching]);

    const editedCompRef = useRef(null);
    const prevEditModeRef = useRef(false);

    useEffect(() => {
        const prevEditMode = prevEditModeRef.current;
        if (prevEditMode === true && isEditMode === false) {
            isMobile && smallHeightDevice ? setNavbarContent(null) : setNavbarContent(<FramkyLogoNavbar />);
            openDrawer(currentComp?.baseline_data ? IMAGE_DRAWER : TEMPLATE_DRAWER, true);
            setFrameOrbitControls(false);
            setEditMode(false);
            setSelectedFrame(null);
            editedCompRef.current = null;
        }
        prevEditModeRef.current = isEditMode;
    }, [isMobile, isEditMode]);

    const openEditMode = async () => {
        pushEvent('edit_mode_open');
        setEditMode(true);
        setIsEditMode(true);
        // setFrameOrbitControls(true);
        editedCompRef.current = currentComp;
        setSelectedFrame(currentComp.frames_json[0]);
        const compositionId = await generateCompositionId();
        const composition = cloneDeep(currentComp);
        composition.id = compositionId
        composition.baseline = composition?.baseline_data ? composition.baseline_data.id : null;
        setCurrentComp(composition);
        setZoomLevel(ZOOM_EDIT);
    }

    const closeEditMode = () => {
        pushEvent('edit_mode_close');

        if (currentComp.favourite) {
            setParsedComps((prevState) => ({
                ...prevState,
                favouriteComps: prevState.favouriteComps.filter((c) => c.id !== currentComp.id)
            }));
        }
        setIsEditMode(false);
        Api.delete(`/composer/composition/${currentComp.id}/`);

        if (editedCompRef.current) {
            setCurrentComp(editedCompRef.current, false);
        }
        setZoomLevel(ZOOM_ROOM);
    };

    const saveEditedComposition = () => {
        pushEvent('edit_mode_save');

        if (editedCompRef.current.favourite) {
            setParsedComps((prevState) => ({
                ...prevState,
                favouriteComps: prevState.favouriteComps.filter((c) => c.id !== editedCompRef.current.id)
            }));
            Api.patch(`/composer/composition/${editedCompRef.current.id}/`, { favourite: false });
        }
        updateCompSize();
        setIsEditMode(false);
        setCurrentTemplate(null);
        setZoomLevel(ZOOM_ROOM);
    }

    useEffect(() => {
        if (isEditMode) {
            openDrawer(FRAMESIZE_DRAWER, true);
        }
    }, [isEditMode]);

    useEffect(() => {
        if (activeFrame && !currentComp.frames_json.some((frame) => frame.id === activeFrame.id)) {
            setSelectedFrame(null)
        }
    }, [activeFrame, currentComp])

    useEffect(() => {
        if (currentComp) {
            const offsetX = (currentComp.width / 2) % 25
            const offsetY = (currentComp.height / 2) % 25
            setGridOffset([offsetX / 1000, offsetY / 1000])
        }
    }, [currentComp]);

    const calculateFramesMenuPosition = useCallback((frames, menuSize) => {
        const [maxX, maxY] = getCompositionMaxCoordinates(frames)
        const [minX, minY] = getCompositionOffsetFromOrigin(frames)
        const x = minX / 1000 - currentComp?.width / 2 / 1000
        const y = maxY / 1000 - currentComp?.height / 2 / 1000 + menuSize * (0.5 + editMenuOffsetYFactor)
        const xMax = maxX / 1000 - currentComp?.width / 2 / 1000 - menuSize * ((editModeFramesMenuCount - 1) * (1 + editMenuGapXFactor) + 0.5)
        const yMin = minY / 1000 - currentComp?.height / 2 / 1000 + menuSize * (1 + editMenuOffsetYFactor)
        return [x, y, xMax, yMin]
    }, [currentComp, editModeFramesMenuCount]);

    const adjustEditMenuPosition = (size) => {
        const menuSize = adjustIconSize(size, 1, cameraRef.current)[0]
        let [worldX, worldY, worldXMax, worldYMin] = calculateFramesMenuPosition(
            activeFrames || [activeFrame], menuSize
        )
        const diffX = Math.max(
            0,
            (editModeFramesMenuCount - 1) * (1 + editMenuGapXFactor) * menuSize - (worldXMax + menuSize * ((editModeFramesMenuCount - 1) * (1 + editMenuGapXFactor) + 0.5) - worldX)
        )
        worldX -= menuSize * 0.5
        worldY += menuSize * 0.5
        const origin = screenToWorld(0, 0, cameraRef.current, 0)
        if (worldX < origin.x || worldY > origin.y) {
            worldX = Math.min(worldXMax + diffX, Math.max(worldX, origin.x))
            worldY = Math.max(worldYMin, Math.min(worldY, origin.y))
        }
        setEditModeFramesMenuPosition([worldX + menuSize * 0.5, worldY - menuSize * 0.5])
        setEditModeFramesMenuSize(menuSize)
    }

    useEffect(() => {
        if (isEditMode && ((activeFrames && activeFrames.length > 0) || activeFrame) && !isSelecting.current && !isGesture) {
            setShowEditModeFramesMenu(true);
            adjustEditMenuPosition(40)
        } else {
            setShowEditModeFramesMenu(false)
        }
    }, [isEditMode, activeFrame, activeFrames, isGesture]);

    const updateEditedFrames = (frames) => {
        if (!isArray(frames)) {
            frames = [frames];
        }
        const updatedFrames = currentComp.frames_json.map((f) => {
            if (frames.some((frame) => frame.id === f.id)) {
                const newFrame = frames.find((frame) => frame.id === f.id)
                if (activeFrame && f.id === activeFrame.id) {
                    setSelectedFrame(newFrame)
                }
                return newFrame
            }
            return f
        })
        if (activeFrames) {
            setActiveFrames(frames)
        }

        editModeUpdateFrames(updatedFrames)
    }

    const updateWallBackground = async (id, payload) => {
        try {
            const response = await Api.patch(`/shopify/svg/walls/${id}/`, payload);
        } catch (error) {
            logError(error);
        }
    };


    const screenToWorld = useCallback((x, y, camera, targetZ) => {
        let vec = new Vector3();
        let pos = new Vector3();


        vec.x = ((x - workspaceOffset.x) / workspaceSize.width) * 2 - 1;
        vec.y = -((y - workspaceOffset.y) / workspaceSize.height) * 2 + 1;
        vec.z = 0.5

        vec.unproject(camera);
        vec.sub(camera.position);
        vec.normalize();

        const distance = (targetZ - camera.position.z) / vec.z;
        pos.copy(camera.position).add(vec.multiplyScalar(distance));

        let result;

        if (!isNaN(pos.x) && !isNaN(pos.y) && !isNaN(pos.z)) {
            result = pos;
        } else {
            result = new Vector3(0, 0, 0.5);
        }
        return result;

    }, [workspaceOffset, workspaceSize]);

    const worldToScreen = useCallback((worldVector, camera) => {
        let vec = new Vector3().copy(worldVector);  // kopiujemy wektor, żeby nie modyfikować oryginalnego

        // przeliczamy na przestrzeń klipu (-1 do 1)
        vec.project(camera);

        // przeliczamy na przestrzeń ekranu
        vec.x = Math.round((vec.x * 0.5 + 0.5) * workspaceSize.width + workspaceOffset.x);
        vec.y = Math.round((-(vec.y * 0.5) + 0.5) * workspaceSize.height);

        return vec;
    }, [workspaceSize, workspaceOffset, isMobile, navbarHeight]);

    // const calculateCameraPosition = useCallback((camera, worldX, worldY, worldZ, screenX, screenY, cameraZ) => {
    //     let vec = new Vector3();

    //     vec.x = ((screenX) / workspaceSize.width) * 2 - 1;
    //     vec.y = -((screenY) / workspaceSize.height) * 2 + 1;
    //     vec.z = 0.5

    //     vec.unproject(camera);
    //     vec.sub(camera.position);
    //     vec.normalize();

    //     let pointingPos = new Vector3(worldX, worldY, worldZ);
    //     const distance = (cameraZ - worldZ) / vec.z;
    //     pointingPos.add(vec.multiplyScalar(distance));
    //     return pointingPos;
    // }, [workspaceSize]);

    const rotateCameraAroundTarget = (camera, target, angleX, angleY, radius) => {
        const x = radius * Math.sin(angleY) * Math.cos(angleX);
        const y = radius * Math.sin(angleX);
        const z = radius * Math.cos(angleY) * Math.cos(angleX);

        camera.position.set(x, y, z);
        camera.lookAt(target);
    };

    const adjustIconSize = (heightPx, ratio, camera) => {
        const orgWorld = screenToWorld(0, 0, camera, 0)
        const world = screenToWorld(0, heightPx, camera, 0)
        const targetHeight = Math.abs(orgWorld.y - world.y)
        const targetWidth = ratio * targetHeight
        return [parseFloat(targetWidth.toFixed(4)), parseFloat(targetHeight.toFixed(4))]
    }

    const getFrameCoordinates = (frame, type = FRAME) => {
        let x, y;
        let width, height;
        const faceWidth = memoizedMaterials.get(frame.frame_material).face_width;
        if (type === FRAME) {
            x = frame.x;
            y = frame.y;
            width = frame.calculated.frame_width;
            height = frame.calculated.frame_height;
        }
        if (type === PADDING) {
            x = frame.x - 25;
            y = frame.y + 25;
            width = frame.calculated.frame_width + (2 * 25);
            height = frame.calculated.frame_height + (2 * 25);
        }
        if (type === PRINT) {
            x = frame.x + faceWidth;
            y = frame.y - faceWidth;
            width = frame.calculated.image_width;
            height = frame.calculated.image_height;
        }
        if (type === PRINTED_IMAGE) {
            x = frame.x + faceWidth + frame.passepartout_width_mm;
            y = frame.y - faceWidth - frame.passepartout_width_mm;
            width = frame.calculated.image_width - 2 * frame.passepartout_width_mm
            height = frame.calculated.image_height - 2 * frame.passepartout_width_mm
        }
        const frame_left = (x - currentComp.width / 2) / 1000
        const frame_right = (x + width - currentComp.width / 2) / 1000
        const frame_top = (y - currentComp.height / 2) / 1000
        const frame_bottom = (y - height - currentComp.height / 2) / 1000
        return { frame_left, frame_right, frame_top, frame_bottom };
    }

    const detectFrame = (worldX, worldY, type = FRAME, excludeEmpty = false, targetFrame = null) => {
        let detectedFrame = null;
        if (currentComp?.frames_json) {
            const frames = targetFrame ? [targetFrame] : currentComp.frames_json;
            for (let frame of frames) {
                if ((!frame.image || baselineImages.includes(frame.image)) && excludeEmpty) continue;
                const { frame_left, frame_right, frame_top, frame_bottom } = getFrameCoordinates(frame, type);

                if (frame_left <= worldX && frame_right >= worldX && frame_top >= worldY && frame_bottom <= worldY) {
                    detectedFrame = frame;
                    break
                }
            }
        }
        return detectedFrame;
    }

    const findNearestFrame = (worldX, worldY, center) => {
        let nearestFrame = null;
        let minDistance = Infinity;

        currentComp?.frames_json.forEach((frame) => {
            let distance = 0
            if (center) {
                // odległość od środka ramki
                const frameCenterX = (frame.x + frame.calculated.frame_width / 2 - currentComp.width / 2) / 1000
                const frameCenterY = (frame.y - frame.calculated.frame_height / 2 - currentComp.height / 2) / 1000;
                const dx = worldX - frameCenterX;
                const dy = worldY - frameCenterY;
                distance = Math.sqrt(dx * dx + dy * dy);
            } else {
                // odległość od krawędzi ramki
                const { frame_left, frame_right, frame_top, frame_bottom } = getFrameCoordinates(frame);
                let minDistanceX, minDistanceY;

                if (worldX < frame_left) {
                    minDistanceX = frame_left - worldX;
                } else if (worldX > frame_right) {
                    minDistanceX = worldX - frame_right;
                } else {
                    minDistanceX = 0;
                }

                if (worldY > frame_top) {
                    minDistanceY = worldY - frame_top;
                } else if (worldY < frame_bottom) {
                    minDistanceY = frame_bottom - worldY;
                } else {
                    minDistanceY = 0;
                }

                distance = Math.sqrt(minDistanceX * minDistanceX + minDistanceY * minDistanceY);
            }
            if (distance < minDistance) {
                minDistance = distance;
                nearestFrame = frame;
            }
        });
        return nearestFrame;
    }

    const haveIntersection = (f1, f2, padding) => {
        const { frame_left, frame_right, frame_top, frame_bottom } = getFrameCoordinates(f2);
        return !(
            +(frame_left - padding).toFixed(4) >= +(f1.x + f1.width + padding).toFixed(4) ||
            +(frame_right + padding).toFixed(4) <= +(f1.x - padding).toFixed(4) ||
            +(frame_bottom - padding).toFixed(4) >= +(f1.y + f1.height + padding).toFixed(4) ||
            +(frame_top + padding).toFixed(4) <= +(f1.y - padding).toFixed(4)
        );
    };

    const checkFrameCollisions = (target, padding = 0.025) => {
        for (let frame of currentComp.frames_json) {
            if (isArray(target)) {
                if (target.some((t) => t.id === frame.id)) continue;
                for (let t of target) {
                    if (haveIntersection(t, frame, padding)) {
                        return true;
                    }
                }
            } else {
                if (frame.id === target.id) continue;
                if (haveIntersection(target, frame, padding)) {
                    return true;
                }
            }
        }
        return false;
    }

    const addFramesToSelection = (selectionArea) => {
        const selectedFrames = [];
        if (Array.isArray(currentComp.frames_json) && currentComp.frames_json.length > 0) {
            for (let frame of currentComp.frames_json) {
                if (haveIntersection(selectionArea, frame, 0)) {
                    selectedFrames.push(frame);
                }
            }
        }
        return selectedFrames;
    }

    const findClosestFit = (frame, dropX, dropY, padding) => {
        // dropX i dropY to współrzędne upuszczenia ramki (środek ramki / world)
        const width = frame.calculated.frame_width / 1000;
        const height = frame.calculated.frame_height / 1000;
        let bestX = 0;
        let bestY = 0;
        let bestDistance = Infinity;

        let startX = dropX - padding;
        let startY = dropY - padding;
        let endX = dropX + padding;
        let endY = dropY + padding;

        while (true) {
            let foundPlaceInThisIteration = false;

            for (let x = startX; x <= endX; x += padding) {
                if (checkPosition(x, startY)) {
                    foundPlaceInThisIteration = true;
                }
            }
            for (let x = startX; x <= endX; x += padding) {
                if (checkPosition(x, endY)) {
                    foundPlaceInThisIteration = true;
                }
            }
            for (let y = startY + padding; y <= endY - padding; y += padding) {  // unikamy podwójnego sprawdzania rogów
                if (checkPosition(startX, y)) {
                    foundPlaceInThisIteration = true;
                }
            }
            for (let y = startY + padding; y <= endY - padding; y += padding) {
                if (checkPosition(endX, y)) {
                    foundPlaceInThisIteration = true;
                }
            }
            if (foundPlaceInThisIteration) {
                return [bestX, bestY];
            }

            startX -= padding;
            startY -= padding;
            endX += padding;
            endY += padding;
        }

        function checkPosition(x, y) {
            if (!checkFrameCollisions({
                id: frame.id,
                x: x - width / 2,
                y: y - height / 2,
                width: width,
                height: height
            })) {
                let distance = dist([x, y], [dropX, dropY]);
                if (distance < bestDistance) {
                    bestDistance = distance;
                    bestX = x;
                    bestY = y;
                    return true;
                }
            }
            return false;
        }
    }

    function* spiralSearch(centerX, centerY, step) {
        let [x, y] = [centerX, centerY];
        yield [x, y];

        let length = 1;
        while (true) {
            for (let i = 0; i < length; i++) {
                x += step;
                yield [x, y];
            }
            for (let i = 0; i < length; i++) {
                y += step;
                yield [x, y];
            }
            length++;
            for (let i = 0; i < length; i++) {
                x -= step;
                yield [x, y];
            }
            for (let i = 0; i < length; i++) {
                y -= step;
                yield [x, y];
            }
            length++;
        }
    }

    function findClosestFitForGroup(mainFrame, frames, dropX, dropY, padding) {
        const checkedPlaces = new Set();

        for (let [x, y] of spiralSearch(dropX, dropY, padding)) {
            const key = `${x}-${y}`;
            if (checkedPlaces.has(key)) continue;
            checkedPlaces.add(key);

            const framesToCheck = [];
            frames.forEach(frame => {
                const width = frame.calculated.frame_width / 1000;
                const height = frame.calculated.frame_height / 1000;
                const dx = (frame.x + width * 1000 / 2 - mainFrame.x - mainFrame.calculated.frame_width / 2) / 1000;
                const dy = (frame.y - height * 1000 / 2 - mainFrame.y + mainFrame.calculated.frame_height / 2) / 1000;

                const positionX = x + dx;
                const positionY = y + dy;

                framesToCheck.push({
                    id: frame.id,
                    x: positionX - width / 2,
                    y: positionY - height / 2,
                    width: width,
                    height: height
                })
            })

            const collision = checkFrameCollisions(framesToCheck, padding);
            if (!collision) return [x, y];
        }
    }

    function createNewFrame(frameSize, x, y, dropX, dropY, params) {
        const material = memoizedMaterials.get(currentFrami?.default_frame_material)
        const cardboardPassepartout = (
            currentFrami.default_cardboard_passepartout && currentFrami.default_passepartout_width !== "None"
        ) ? memoizedCardboardPassepartouts.get(currentFrami.default_cardboard_passepartout) : null;
        const imageWidth = frameSize.width - (2 * material.face_width)
        const imageHeight = frameSize.height - (2 * material.face_width)

        let passepartoutWidth = getPassepartoutBestFit(
            currentFrami.default_passepartout_width,
            imageWidth,
            imageHeight
        );

        for (let frame of currentComp.frames_json) {
            if (getNormalizedFrameSize(frame) === getNormalizedFrameSize({ frame_size: frameSize.id })) {
                passepartoutWidth = frame.passepartout_width_mm;
                break;
            }
        }

        if (x == null || y == null) {
            const [worldX, worldY] = findClosestFit(
                { id: null, calculated: { frame_width: frameSize.width, frame_height: frameSize.height } },
                dropX,
                dropY,
                0.025
            )
            x = Math.round(worldX * 1000 + currentComp.width / 2 - frameSize.width / 2)
            y = Math.round(worldY * 1000 + currentComp.height / 2 + frameSize.height / 2)
        }
        let newFrame = {
            id: generateRandomId(),
            x: x,
            y: y,
            frame_top: currentComp.height - y,
            frame_left: x,
            frame_material: material.id,
            frame_size: frameSize.id,
            image: null,
            cropping: "0,0,1,1",
            filter: currentFrami.images_filter,
            passepartout_width_mm: passepartoutWidth,
            passepartout_color: cardboardPassepartout ? cardboardPassepartout.color : "#FFFFFF",
            cardboard_passepartout: currentFrami.default_passepartout_width !== "None"
                ? currentFrami.default_cardboard_passepartout
                : null,
            calculated: {
                image_top: currentComp.height - y + material.face_width,
                image_left: x + material.face_width,
                image_width: frameSize.width - (2 * material.face_width),
                image_height: frameSize.height - (2 * material.face_width),
                frame_width: frameSize.width,
                frame_height: frameSize.height,
                dpi: 0,
            }
        }
        if (params) {
            newFrame = { ...newFrame, ...params }
        }
        return newFrame;
    }

    const addFrame = (frameSize, x, y, dropX = 0, dropY = 0, params) => {
        const frames = currentComp.frames_json.slice()
        const newFrame = createNewFrame(frameSize, x, y, dropX, dropY, params);
        frames.push(newFrame)
        editModeUpdateFrames(frames)
        setSelectedFrame(newFrame)
    }

    const copyFrames = (frames) => {
        let duplicatedFrames = [];
        frames.forEach((frame) => {
            const frameSize = memoizedFrameSizes.get(frame.frame_size);
            const x = (frame.x + frameSize.width / 2 - currentComp.width / 2) / 1000
            const y = (frame.y - frameSize.height / 2 - currentComp.height / 2) / 1000
            const duplicatedParams = {
                frame_material: frame.frame_material,
                passepartout_color: frame.passepartout_color,
                passepartout_width_mm: frame.passepartout_width_mm,
                filter: frame.filter,
            }
            const newFrame = createNewFrame(frameSize, frame.x, frame.y, x, y, duplicatedParams)
            duplicatedFrames.push(newFrame)
        })
        return duplicatedFrames;
    }

    const recalculateFramesPosition = (mainFrame, duplicatedFrames) => {
        const dropX = (mainFrame.x + mainFrame.calculated.frame_width / 2 - currentComp.width / 2) / 1000
        const dropY = (mainFrame.y - mainFrame.calculated.frame_height / 2 - currentComp.height / 2) / 1000
        if (duplicatedFrames.length === 1) {
            const [worldX, worldY] = findClosestFit(
                {
                    id: null,
                    calculated: {
                        frame_width: mainFrame.calculated.frame_width,
                        frame_height: mainFrame.calculated.frame_height
                    }
                },
                dropX,
                dropY,
                0.025
            )
            const x = Math.round(worldX * 1000 + currentComp.width / 2 - mainFrame.calculated.frame_width / 2)
            const y = Math.round(worldY * 1000 + currentComp.height / 2 + mainFrame.calculated.frame_height / 2)

            return [{
                ...duplicatedFrames[0],
                x: x,
                y: y,
                frame_top: currentComp.height - y,
                frame_left: x,
            }]
        } else {
            const [bestX, bestY] = findClosestFitForGroup(
                mainFrame,
                duplicatedFrames,
                dropX,
                dropY,
                0.025
            )
            return duplicatedFrames.map((frame) => {
                const dx = frame.x + frame.calculated.frame_width / 2 - (mainFrame.x + mainFrame.calculated.frame_width / 2)
                const dy = frame.y - frame.calculated.frame_height / 2 - (mainFrame.y - mainFrame.calculated.frame_height / 2)

                const worldX = bestX + dx / 1000
                const worldY = bestY + dy / 1000

                return {
                    ...frame,
                    frame_left: Math.round(1000 * worldX + currentComp.width / 2 - frame.calculated.frame_width / 2),
                    frame_top: Math.round(currentComp.height / 2 - 1000 * worldY - frame.calculated.frame_height / 2),
                    x: Math.round(1000 * worldX + currentComp.width / 2 - frame.calculated.frame_width / 2),
                    y: Math.round(currentComp.height / 2 + 1000 * worldY + frame.calculated.frame_height / 2),

                }
            })
        }
    }

    const pasteFrames = (duplicatedFrames) => {
        const compFrames = currentComp.frames_json.slice()
        const updatedFrames = compFrames.concat(duplicatedFrames)
        editModeUpdateFrames(updatedFrames)
        if (duplicatedFrames.length > 1) {
            setActiveFrames(duplicatedFrames)
        } else {
            setSelectedFrame(duplicatedFrames[0])
        }
    }

    const duplicateFrames = (frames) => {
        if (!Array.isArray(frames)) {
            frames = [frames];
        }
        let duplicatedFrames = copyFrames(frames);
        const mainFrame = frames[0]
        duplicatedFrames = recalculateFramesPosition(mainFrame, duplicatedFrames);
        pasteFrames(duplicatedFrames);
    }

    const copyFramesToClipboard = (frames) => {
        if (!Array.isArray(frames)) {
            frames = [frames];
        }
        let duplicatedFrames = copyFrames(frames);
        setFramesClipboard(duplicatedFrames);
    }

    const pasteFramesFromClipboard = () => {
        if (framesClipboard) {
            let duplicatedFrames = copyFrames(framesClipboard);
            const mainFrame = framesClipboard[0]
            duplicatedFrames = recalculateFramesPosition(mainFrame, duplicatedFrames);
            pasteFrames(duplicatedFrames);
        }
    }

    const deleteFrames = (frameIds) => {
        if (!isArray(frameIds)) {
            frameIds = [frameIds];
        }
        const frames = currentComp.frames_json.slice()
        frameIds.forEach((frameId) => {
            const item = frames.find((i) => i.id === frameId);
            const index = frames.indexOf(item);
            frames.splice(index, 1);
        });
        editModeUpdateFrames(frames)
    }

    const rotateFrames = (frames, clockwise = true) => {
        if (!Array.isArray(frames)) {
            frames = [frames];
        }
        const isGroup = frames.length > 1;
        const [minX, minY] = getCompositionOffsetFromOrigin(frames)
        const [maxX, maxY] = getCompositionMaxCoordinates(frames)
        let centerX = (minX + maxX) / 2;
        let centerY = (minY + maxY) / 2;
        const rotatedFrames = [];

        let updatedFrames = currentComp.frames_json.map(f => {
            if (frames.some((frame) => frame.id === f.id)) {
                let newFrameSizeId;
                let prevFrameSize = memoizedFrameSizes.get(f.frame_size);
                if (prevFrameSize.id.endsWith('L')) {
                    newFrameSizeId = prevFrameSize.id.slice(0, -1);
                } else if (prevFrameSize.width === prevFrameSize.height) {
                    newFrameSizeId = prevFrameSize.id;
                } else {
                    newFrameSizeId = f.frame_size + 'L';
                }
                const newWidth = prevFrameSize.height;
                const newHeight = prevFrameSize.width;

                const dx = f.x - centerX;
                const dy = f.y - centerY;

                let newX, newY;
                if (clockwise) {
                    newX = centerX + dy - newWidth;
                    newY = centerY - dx;
                } else {
                    newX = centerX - dy;
                    newY = centerY + dx + newHeight;
                }

                if (!isGroup) {
                    const worldX = (newX + newWidth / 2 - currentComp.width / 2) / 1000;
                    const worldY = (newY - newHeight / 2 - currentComp.height / 2) / 1000;

                    const collision = checkFrameCollisions({
                        id: f.id,
                        x: worldX - newWidth / 1000 / 2,
                        y: worldY - newHeight / 1000 / 2,
                        width: newWidth / 1000,
                        height: newHeight / 1000
                    })

                    if (collision) {
                        const [bestX, bestY] = findClosestFit(
                            { id: f.id, calculated: { frame_width: newWidth, frame_height: newHeight } },
                            worldX,
                            worldY,
                            0.025
                        )
                        newX = bestX * 1000 + currentComp.width / 2 - newWidth / 2
                        newY = bestY * 1000 + currentComp.height / 2 + newHeight / 2
                    }
                }

                let newFrame = {
                    ...f,
                    frame_size: newFrameSizeId,
                    x: newX,
                    y: newY,
                    frame_top: currentComp.height - newY,
                    frame_left: newX,
                };
                const faceWidth = memoizedMaterials.get(f.frame_material).face_width;
                const frameImage = images.find(i => i.filename === f.image);
                const sizes = {
                    ...newFrame.calculated,
                    image_width: newWidth - (2 * faceWidth),
                    image_height: newHeight - (2 * faceWidth),
                    frame_width: newWidth,
                    frame_height: newHeight,
                }
                newFrame = { ...newFrame, calculated: sizes };
                newFrame = { ...newFrame, cropping: updateCrop(images.find(i => i.filename === newFrame.image), newFrame) };
                const cropZoomLimit = calculateCropZoomLimit(newFrame, frameImage, faceWidth);
                const calculated = {
                    ...newFrame.calculated,
                    dpi: calculateDpi(newFrame, faceWidth),
                    crop_zoom: Math.max(calculateCropZoom(newFrame, frameImage), cropZoomLimit),
                    crop_zoom_min: cropZoomLimit
                };
                newFrame = { ...newFrame, calculated: calculated };
                if (activeFrame) {
                    setActiveFrame(newFrame);
                }
                rotatedFrames.push(newFrame);
                return newFrame;
            }
            return f;
        })
        if (isGroup) {
            const mainFrame = rotatedFrames.slice()[0]
            const framesToUpdate = recalculateFramesPosition(mainFrame, rotatedFrames)
            const remainingFrames = updatedFrames.filter((frame) => {
                return !framesToUpdate.some((f) => f.id === frame.id)
            })
            updatedFrames = remainingFrames.concat(framesToUpdate)
            setActiveFrames(framesToUpdate)
        }
        editModeUpdateFrames(updatedFrames);
    }

    const flipFrames = (frames, type = "X") => {
        if (!Array.isArray(frames)) {
            return
        }
        const [maxX, maxY] = getCompositionMaxCoordinates(frames)
        const [minX, minY] = getCompositionOffsetFromOrigin(frames)

        let flippedFrames = [];

        let updatedFrames = currentComp.frames_json.map(f => {
            let newFrame;
            if (frames.some((frame) => frame.id === f.id)) {
                if (type === "X") {
                    const x = maxX - (f.x - minX) - f.calculated.frame_width;
                    newFrame = { ...f, x: x, frame_left: x }
                } else if (type === "Y") {
                    const y = maxY - (f.y - minY) + f.calculated.frame_height;
                    newFrame = { ...f, y: y, frame_top: currentComp.height - y }
                }
                flippedFrames.push(newFrame)
                return newFrame
            }
            return f;
        })
        const mainFrame = flippedFrames.slice()[0]
        const framesToUpdate = recalculateFramesPosition(mainFrame, flippedFrames)
        const remainingFrames = updatedFrames.filter((frame) => {
            return !framesToUpdate.some((f) => f.id === frame.id)
        })
        updatedFrames = remainingFrames.concat(framesToUpdate)
        setActiveFrames(framesToUpdate)
        editModeUpdateFrames(updatedFrames);
    }

    const flipComposition = (type) => {
        const frames = currentComp.frames_json.map((frame) => {
            const x = currentComp.width - frame.x - frame.calculated.frame_width;
            const y = currentComp.height - frame.y + frame.calculated.frame_height;
            if (type === "X") {
                return { ...frame, x: x, frame_left: x }
            } else if (type === "Y") {
                return { ...frame, y: y, frame_top: currentComp.height - y }
            } else if (type === "XY") {
                return { ...frame, x: x, frame_left: x, y: y, frame_top: currentComp.height - y }
            } else {
                return frame;
            }
        })
        editModeUpdateFrames(frames);
    }

    useEffect(() => {
        if (compositionModelLoaded && openModelViewer) {
            if (currentComp?.lut_filter && currentComp?.lut_filter !== 'NONE') {
                prepareFilteredImages();
            } else {
                setIsExportingGLTF(true);
                searchParams.delete('model_viewer');
                setSearchParams(searchParams);
            }
        }
    }, [compositionModelLoaded, openModelViewer, currentComp]);


    useEffect(() => {
        if (filteredImages && openModelViewer) {
            setIsExportingGLTF(true);
        }
    }, [filteredImages, openModelViewer, searchParams]);

    const updateDimensionTextSizes = (camera) => {
        const point1 = screenToWorld(0, 0, camera, 0);
        const point2 = screenToWorld(0, isMobile ? 16 : 20, camera, 0);

        const distance = point1.distanceTo(point2);
        setDimensionTextSizeWorld(distance);
        setDimensionTextOffsetWorld(distance);
        setDimensionLineOffsetWorld(Math.max(isMobile ? distance * 1 : distance * 1.8, 0.015));
        setDiagonalLineLengthWorld(Math.max(distance * 0.4, 0.0075));
    };

    const getWallBoundaries = useCallback((wallBackground = null, useSafeZone = false) => {
        wallBackground = wallBackground || framiWallBackground;
        if (!wallBackground) return { topLeft: new Vector3(-1, 1, 0), bottomRight: new Vector3(1, -1, 0) };
        
        const pixelsToMeters = (pixels) => pixels / wallBackground.px_per_100cm * 1;

        const source = useSafeZone
            ? wallBackground.scaling_data?.safe_zone
            : {
                  x: 0,
                  y: 0,
                  width: wallBackground.original_width,
                  height: wallBackground.original_height,
              };

        if (!source) return { topLeft: new Vector3(-1, 1, 0), bottomRight: new Vector3(1, -1, 0) };

        // Calculate boundaries based on composition center and image dimensions
        const centerX = wallBackground.composition_center_x;
        const centerY = wallBackground.composition_center_y;
        const width = source.width;
        const height = source.height;

        const safeZoneLeftPx = source.x - centerX;
        const safeZoneTopPx = source.y - centerY;

        const leftEdge = pixelsToMeters(safeZoneLeftPx);
        const rightEdge = pixelsToMeters(safeZoneLeftPx + width);
        const topEdge = -pixelsToMeters(safeZoneTopPx);
        const bottomEdge = -pixelsToMeters(safeZoneTopPx + height);
        
        return {
            topLeft: new Vector3(leftEdge, topEdge, 0),
            bottomRight: new Vector3(rightEdge, bottomEdge, 0)
        };
    }, [framiWallBackground]);

    const calculateWallBackgroundCoordinates = useCallback((wallBackground = null, padding = 0) => {
        const { topLeft, bottomRight } = getWallBoundaries(wallBackground, activeDrawer !== WALLBACKGROUND_DRAWER);

        const p1 = new Vector3(topLeft.x - padding, topLeft.y + padding, 0);
        const p2 = new Vector3(bottomRight.x + padding, bottomRight.y - padding, 0);
        return { p1, p2 };
    }, [getWallBoundaries, activeDrawer]);

    const calculateCompositionCoordinates = useCallback((includeDimensions = true, composition = null) => {
        composition = composition || currentComp;
        if (!composition) return { p1: new Vector3(-1, 1, 0), p2: new Vector3(1, -1, 0) };
        const width = composition.width / 1000;
        const height = composition.height / 1000;

        let dimensionOffset = 0;
        if (includeDimensions) {
            dimensionOffset = dimensionTextSizeWorld / 2 + dimensionTextOffsetWorld + dimensionLineOffsetWorld;
        }
        const p1 = new Vector3(-width / 2, height / 2, frameDepth);
        const p2 = new Vector3(width / 2 + dimensionOffset, -height / 2 - dimensionOffset, frameDepth);
        return { p1, p2 };
    }, [currentComp, dimensionTextSizeWorld, dimensionTextOffsetWorld, dimensionLineOffsetWorld]);

    const correctCameraPosition = useCallback((position, lookAt) => {
        if (!cameraRef.current || !framiWallBackground) return { correctedPosition: position, correctedLookAt: lookAt };
        const tempCamera = cameraRef.current.clone();
        tempCamera.position.copy(position);
        tempCamera.lookAt(lookAt);
        tempCamera.updateProjectionMatrix();

        const worldTopLeft = screenToWorld(0, 0, tempCamera, 0);
        const worldBottomRight = screenToWorld(workspaceSize.width, workspaceSize.height, tempCamera, 0);

        const { topLeft: wallTopLeft, bottomRight: wallBottomRight } = getWallBoundaries();

        const dxLeft = wallTopLeft.x - worldTopLeft.x;
        const dxRight = wallBottomRight.x - worldBottomRight.x;
        const dyTop = wallTopLeft.y - worldTopLeft.y;
        const dyBottom = wallBottomRight.y - worldBottomRight.y;

        let dx = 0;
        if (dxRight < 0) dx = dxRight;
        if (dxLeft > 0) dx = dxLeft;

        let dy = 0;
        if (dyBottom > 0) dy = dyBottom;
        if (dyTop < 0) dy = dyTop;

        return {
            correctedPosition: position.clone().add(new Vector3(dx, dy, 0)),
            correctedLookAt: lookAt.clone().add(new Vector3(dx, dy, 0)),
        };
    }, [cameraRef, workspaceSize, getWallBoundaries, screenToWorld]);

    const updateCamera = useCallback((zoomLevel, targetFrame = null) => {
        console.log("updateCamera", zoomLevel, targetFrame);
        if (isEditMode) return;
        
        let position, lookAt;
        
        switch (zoomLevel) {
            case ZOOM_ROOM: {
                if (!framiWallBackground) return;
                const { p1, p2 } = calculateWallBackgroundCoordinates();
                ({ position, lookAt } = calculateCameraPosition(p1, p2, false, 1));
                break;
            }
            
            case ZOOM_COMPOSITION: {
                if (!currentComp?.frames_json) return;
                const { p1, p2 } = calculateCompositionCoordinates(true);
                ({ position, lookAt } = calculateCameraPosition(p1, p2, true, 1.1));
                const { correctedPosition, correctedLookAt } = correctCameraPosition(position, lookAt);
                position = correctedPosition;
                lookAt = correctedLookAt;
                break;
            }
            
            case ZOOM_FRAME: {
                if (!targetFrame) return;
                const minX = targetFrame.frame_left / 1000 - currentComp.width / 1000 / 2;
                const maxY = targetFrame.y / 1000 - currentComp.height / 1000 / 2;
                const maxX = minX + targetFrame.calculated.frame_width / 1000;
                const minY = maxY - targetFrame.calculated.frame_height / 1000;

                ({ position, lookAt } = calculateCameraPosition(
                    new Vector3(minX, maxY, frameDepth),
                    new Vector3(maxX, minY, frameDepth),
                    true,
                    1.05
                ));
                break;
            }
            
            case ZOOM_EDIT: {
                if (!currentComp?.frames_json) return;
                const { p1, p2 } = calculateCompositionCoordinates(true);
                ({ position, lookAt } = calculateCameraPosition(p1, p2, true, 1.40));
                const { correctedPosition, correctedLookAt } = correctCameraPosition(position, lookAt);
                position = correctedPosition;
                lookAt = correctedLookAt;
                break;
            }
            
            default:
                return;
        }
        
        setCameraPosition(position);
        setCameraLookAt(lookAt);
    }, [isEditMode, currentComp, calculateWallBackgroundCoordinates, calculateCompositionCoordinates,
        calculateCameraPosition, correctCameraPosition, frameDepth]);


    // useEffect do obsługi activeFrame
    useEffect(() => {
        if (isEditMode) return;

        if (activeFrame) {
            updateCamera(ZOOM_FRAME, activeFrame);
        } else  {
            if (framiWallBackground) {
                updateCamera(zoomLevel);
            } else {
                updateCamera(ZOOM_COMPOSITION)
            }
        }
    }, [activeFrame, isEditMode, zoomLevel, updateCamera, framiWallBackground]);

    const captureHighResImage = (gl, scene, camera, productId, type, variant) => {
        return new Promise((resolve) => {
            console.log("Rozpoczynam renderowanie wysokiej rozdzielczości obrazu...");

            // Nie wiem czy to jest potrzebne
            gl.outputEncoding = sRGBEncoding;
            gl.toneMapping = ACESFilmicToneMapping;
            gl.toneMappingExposure = 1;

            const originalAspect = camera.aspect;
            camera.aspect = 1;
            camera.updateProjectionMatrix();

            const renderTarget = new WebGLRenderTarget(2048, 2048, {
                minFilter: LinearFilter,
                magFilter: LinearFilter,
                format: RGBAFormat,
                depthBuffer: true,
            });
            // renderTarget.texture.encoding = LinearEncoding;
            renderTarget.texture.encoding = sRGBEncoding;

            gl.autoClear = false;

            // Renderowanie do renderTarget
            gl.setRenderTarget(renderTarget);
            gl.clear(true, true, true);  // Wyczyść kolor, głębię i stencil przed renderowaniem
            gl.render(scene, camera);
            gl.setRenderTarget(null);  // Resetowanie do ekranu

            console.log("Renderowanie zakończone, pobieram piksele...");

            camera.aspect = originalAspect;
            camera.updateProjectionMatrix();

            const canvas = document.createElement('canvas');
            canvas.width = 2048;
            canvas.height = 2048;
            const context = canvas.getContext('2d');

            const pixels = new Uint8Array(2048 * 2048 * 4);
            // gl.readPixels(0, 0, 2048, 2048, gl.RGBA, gl.UNSIGNED_BYTE, pixels);
            // const webglContext = gl.getContext();
            // webglContext.readPixels(0, 0, 2048, 2048, webglContext.RGBA, webglContext.UNSIGNED_BYTE, pixels);
            gl.readRenderTargetPixels(renderTarget, 0, 0, 2048, 2048, pixels);

            const flippedPixels = new Uint8Array(pixels.length);
            const rowSize = 2048 * 4; // 4 wartości na piksel (RGBA)

            for (let y = 0; y < 2048; y++) {
                const srcRow = pixels.subarray(y * rowSize, (y + 1) * rowSize);
                const dstRow = flippedPixels.subarray((2047 - y) * rowSize, (2048 - y) * rowSize);
                dstRow.set(srcRow);
            }

            const imageData = new ImageData(new Uint8ClampedArray(flippedPixels), 2048, 2048);
            context.putImageData(imageData, 0, 0);

            let format = 'image/jpeg';
            if (type === 'preview') {
                format = 'image/png';
            }

            canvas.toBlob((blob) => {
                uploadImageToBackend(blob, productId, type, variant).then(() => {
                    resolve();
                });
            }, format);
        });
    };

    const uploadImageToBackend = async (blob, productId, type, variant) => {
        const formData = new FormData();
        formData.append('image', blob);
        formData.append("product_id", productId);
        formData.append("image_type", type);
        formData.append("size", variant.size);
        formData.append("style", variant.style);

        try {
            const response = await Api.post('/composer/upload-composition-image/', formData);
            console.log('Obraz zapisany na backendzie:', type, variant.size, variant.style);
        } catch (error) {
            console.error('Błąd przesyłania obrazu:', error);
        }
    };

    const getSteps = (composition, wallBackground) => {
        const wallPoints = calculateWallBackgroundCoordinates(wallBackground);
        const wallCameraPosition = calculateCameraPosition(wallPoints.p1, wallPoints.p2, false, 1);
        const compositionPointsWithDimensions = calculateCompositionCoordinates(true, composition);
        const dimensionCameraPosition = calculateCameraPosition(
            compositionPointsWithDimensions.p1, compositionPointsWithDimensions.p2, true, 1.1
        );
        const compositionPoints = calculateCompositionCoordinates(false, composition);
        const compositionCameraPosition = calculateCameraPosition(
            compositionPoints.p1, compositionPoints.p2, true, 1.1
        );

        return [
            {
                name: "interior",
                cameraPosition: wallCameraPosition.position,
                showPhotos: true,
                showDimensions: false,
                showWallBackground: true,
            },
            {
                name: "main",
                cameraPosition: correctCameraPosition(
                    compositionCameraPosition.position, compositionCameraPosition.lookAt
                ).correctedPosition,
                showPhotos: true,
                showDimensions: false,
                showWallBackground: true,
            },
            {
                name: "preview",
                cameraPosition: compositionCameraPosition.position,
                showPhotos: true,
                showDimensions: false,
                showWallBackground: false,
            },
            {
                name: "dimensions",
                cameraPosition: correctCameraPosition(
                    dimensionCameraPosition.position, dimensionCameraPosition.lookAt
                ).correctedPosition,
                showPhotos: false,
                showDimensions: true,
                showWallBackground: true,
            },
        ];
    };

    const generateImagesForAllVariants = async (productId, variants) => {
        const gl = glRef.current;
        const scene = sceneRef.current;
        const camera = cameraRef.current;

        if (!gl || !scene || !camera) {
            console.warn('Brak referencji do gl / scene / camera');
            return;
        }

        setIsGeneratingImages(true);
        const totalSteps = variants.length * 4;
        setImageGenerationProgress({ current: 0, total: totalSteps });

        const sortedVariants = [
            currentVariant,
            ...variants.filter((v) => v.composition_id !== currentVariant?.composition_id),
        ];

        let current = 0;
        const previousVariant = {...currentVariant};

        for (const variant of sortedVariants) {
            setCurrentCompId(variant.composition_id);
            setCurrentVariant(variant);
            await new Promise((resolve) => setTimeout(resolve, 1500));

            const composition = Object.values(parsedComps)
                .flat()
                .find((comp) => comp.id === variant.composition_id);
            const wallBackground = memoizedWallBackgrounds.get(variant.wall_background);

            const steps = getSteps(composition, wallBackground);
            console.log("Generuję obrazy dla wariantu:", variant.size, variant.style, variant.composition_id, steps);

            for (const step of steps) {
                console.log("Przechodzę do etapu:", step.name, step.cameraPosition);
                camera.position.set(...step.cameraPosition);
                camera.updateProjectionMatrix();

                setShowPhotos(step.showPhotos);
                setShowDimensions(step.showDimensions);
                setShowWallBackground(step.showWallBackground);

                await new Promise((resolve) => setTimeout(resolve, 600));

                await captureHighResImage(gl, scene, camera, productId, step.name, variant);

                current += 1;
                setImageGenerationProgress({ current, total: totalSteps });
            }
        }

        setShowPhotos(true);
        setShowDimensions(true);
        setShowWallBackground(true);
        setCurrentCompId(previousVariant.composition_id);
        setCurrentVariant(previousVariant);
        camera.position.set(cameraPosition.x, cameraPosition.y, cameraPosition.z);
        camera.updateProjectionMatrix();

        setIsGeneratingImages(false);
        setImageGenerationProgress({ current: 0, total: 0 })
    };

    const value = {
        hoveredFrame,
        nearestFrame,
        setNearestFrame,
        setHoveredFrame,
        frameOrbitControls,
        setFrameOrbitControls,
        stopCameraAnimation,
        setStopCameraAnimation,
        showActiveFrameCropHint,
        setShowActiveFrameCropHint,
        updateIconSize,
        setUpdateIconSize,
        workspaceWidth: workspaceSize.width,
        workspaceHeight: workspaceSize.height,
        workspaceOffset,
        setWorkspaceOffset,
        workspaceSize,
        setWorkspaceSize,
        workspaceAspectRatio, // Add this to the context value
        workspaceMessagesSectionHeight,
        workspaceToolsSectionHeight,
        // workspaceToolsRelativeHeight,
        workspaceHeightRef,
        workspaceWidthRef,
        isGesture,
        setIsGesture,
        isDragging,
        dropTargetFrame,
        setDropTargetFrame,
        isPanning,
        isSelecting,
        isTransforming,
        isMultiTouching,
        isOrbiting,
        gridOffset,
        mobileToolbar,
        cropZoomSlider,
        communicationBars,
        dotsSection,
        cameraOffsetY,
        cameraOffsetYRef,
        cameraOffsetZ,
        cameraOffsetZRef,
        setCameraOffsetY,
        setCameraOffsetZ,
        isAnimating,
        setIsAnimating,
        setIsOrbiting,
        showTools,
        setShowTools,
        screenToWorld,
        worldToScreen,
        calculateCameraOffsetY,
        calculateCameraExtraY,
        rotateCameraAroundTarget,
        adjustEditMenuPosition,
        adjustIconSize,
        addFramesToSelection,
        handleCropSliderChange,
        detectFrame,
        findNearestFrame,
        checkFrameCollisions,
        findClosestFit,
        findClosestFitForGroup,
        addFrame,
        deleteFrames,
        duplicateFrames,
        rotateFrames,
        flipFrames,
        flipComposition,
        copyFramesToClipboard,
        pasteFramesFromClipboard,
        framesClipboard,
        cameraRef,
        glRef,
        sceneRef,
        isEditMode,
        setIsEditMode,
        showEditModeFramesMenu,
        setShowEditModeFramesMenu,
        editModeFramesMenuPosition,
        setEditModeFramesMenuPosition,
        editModeFramesMenuSize,
        setEditModeFramesMenuSize,
        editModeFramesMenuCount,
        setEditModeFramesMenuCount,
        openEditMode,
        closeEditMode,
        saveEditedComposition,
        updateEditedFrames,
        openNewsletterTooltip,
        compositionModelLoaded,
        setCompositionModelLoaded,
        isExportingGLTF,
        setIsExportingGLTF,
        dimensionTextSizeWorld,
        dimensionTextOffsetWorld,
        dimensionLineOffsetWorld,
        diagonalLineLengthWorld,
        updateDimensionTextSizes,
        zoomLevel,
        setZoomLevel,
        cameraPosition,
        cameraLookAt,
        getWallBoundaries,
        updateWallBackground,
        isScaling,
        setIsScaling,
        calculateCameraPosition,
        calculateWallBackgroundCoordinates,
        calculateCompositionCoordinates,
        showPhotos,
        showDimensions,
        showWallBackground,
        generateImagesForAllVariants,
        updateCamera,
    };

    return (
        <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>
    );
}

// Funkcja pomocnicza: projekcja punktu na płaszczyznę
function projectPointOntoPlane(point, planeNormal, planeD) {
    const t = -(point.dot(planeNormal) + planeD) / planeNormal.lengthSq();
    return point.clone().add(planeNormal.clone().multiplyScalar(t));
}

WorkspaceProvider.propTypes = {
    workspaceRef: PropTypes.object,
    workspaceToolsSectionRef: PropTypes.object,
    workspaceMessagesSectionRef: PropTypes.object,
    isExportingGLTF: PropTypes.bool,
    setIsExportingGLTF: PropTypes.func,
    children: PropTypes.node.isRequired
};
